import * as React from "react";

import { Box, type BoxProps } from "../../components/ui/box";
import type { IconName } from "../../components/ui/icon";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useRevealOnTouchGate } from "../../hooks/use-reveal-on-touch-gate";
import { useOutsideClick } from "../../hooks/use-outside-click";
import type { Omittable } from "../../lib/props";
import {
  InlineEditingCell,
  InlineRowActions,
  InlineValueAction,
  InlineValueCell,
  InlineValueTrigger,
  InlineValueFillGroup,
  type InlineRowActionsVisibility,
} from "./inline-row";

/**
 * The orchestrated inline-edit VALUE cell: one component owning every
 * behavior a table/row cell needs to view and edit a single value —
 * rest-display/editor switching, touch tap-to-reveal, outside-click
 * dismissal with invalid-draft discard, host-highlight lifecycle, the
 * hover-revealed value-actions row, overlay open/close reporting,
 * validation gating (an invalid commit vetoes deactivation; clicking away
 * is the give-up gesture), and a baseline snapshot behind `reset`.
 *
 * Everything value-specific is injected: the display node, the editor (one
 * of three composition kinds below), the action descriptors, and the
 * `validate` callback. The cell never interprets `value` — it only
 * snapshots it for `reset` and passes commits through.
 *
 * Editor kinds:
 * - `inline`   — the host drives `isActive`; at rest the cell is a button
 *                that requests activation, while active the injected editor
 *                renders inside `InlineEditingCell` and commits/deactivates
 *                through its api.
 * - `overlay`  — the editor is a floating surface (popover, drawer, picker)
 *                anchored on the cell; open state lives here and reports
 *                through `onOverlayChange`. The render callback composes the
 *                overlay around a cell body built by `cellWithTriggerPart`
 *                (a Base UI trigger part) or `cellWithTrigger` (a plain
 *                button). `defer` mounts the overlay component only while
 *                open — for editors that are expensive at rest.
 * - `content`  — the value edits in place (a toggle, a tile grid): the
 *                injected content replaces the display with no activation
 *                step.
 */

function blurActiveElement(): void {
  if (typeof document === "undefined") return;
  (document.activeElement as HTMLElement | null)?.blur?.();
}

/** Imperative surface handed to action descriptors and host chrome. */
export type EditableValueCellApi = {
  /** Validate-and-commit. `undefined` means "remove the value" and always
   *  passes (a removal cannot be invalid). */
  commit: (value: unknown) => void;
  /** Deactivation request; vetoed while the last commit was invalid. */
  deactivate: () => void;
  /** Write a known-good value WITHOUT validating, close any overlay, exit
   *  edit mode and drop focus — the shared shape of "clear"-style
   *  affordances that restore a constructed-safe value. */
  overwrite: (value: unknown) => void;
  /** Revert to the value snapshotted when edit mode was entered. */
  reset: () => void;
};

export type EditableValueCellAction = Omittable<{
  key: string;
  icon: IconName;
  /** Accessible label ("Copy email", "Clear value"). */
  label: string;
  /** Hover tooltip on the action button ("Copy"). Required: the button is
   *  icon-only, so this is the only visible name it has. A bare verb — the
   *  full `label` ("Copy email") stays the accessible name. */
  tooltip: string;
  onSelect: (cell: EditableValueCellApi) => void;
}>;

export type EditableValueCellInlineEditorApi = {
  commit: (value: unknown) => void;
  deactivate: () => void;
  /** The last commit attempt was rejected by `validate`, or the editor has
   *  reported a draft error. */
  invalid: boolean;
  /**
   * An editor that refuses edits in place (a formatted number turning down a
   * third decimal) reports the refusal here with its message, `null` once
   * it clears. The cell paints the editing ring destructive and surfaces the
   * message through `onValidationErrorChange`. Unlike a rejected commit it
   * never vetoes deactivation — the draft the editor kept is still valid.
   */
  reportDraftError: (message: string | null) => void;
};

export type EditableValueCellOverlayApi = {
  open: boolean;
  /**
   * Open/close request routed through the touch reveal gate. Returns false
   * when the gate absorbed an open request to reveal the value actions
   * instead — a render adding its own open-edge side effects (draft
   * snapshots) must skip them on an absorbed request.
   */
  onOpenChange: (open: boolean) => boolean;
  close: () => void;
  commit: (value: unknown) => void;
  /** Deactivation request; vetoed while the last commit was invalid. */
  deactivate: () => void;
  /**
   * The standard rest cell wearing the given overlay trigger part (a
   * `PopoverTrigger`/`SelectTrigger`-style element that accepts `render`).
   * The part is cloned with the cell's own trigger button as its `render`,
   * display node inside — so every overlay editor's rest state is
   * byte-identical chrome.
   */
  cellWithTriggerPart: (
    part: React.ReactElement<{ render?: React.ReactElement }>,
  ) => React.ReactNode;
  /** The standard rest cell as a plain button that opens the overlay on
   *  click. `onClick` replaces the default open request, for renders whose
   *  open edge carries side effects — route back through `onOpenChange`. */
  cellWithTrigger: (onClick?: () => void) => React.ReactNode;
};

export type EditableValueCellContentApi = {
  commit: (value: unknown) => void;
};

export type EditableValueCellEditor =
  | Omittable<{
      kind: "inline";
      render: (api: EditableValueCellInlineEditorApi) => React.ReactNode;
    }>
  | Omittable<{
      kind: "overlay";
      render: (api: EditableValueCellOverlayApi) => React.ReactNode;
      /**
       * Mount the overlay component only while it is open; at rest the cell
       * renders `cellWithTrigger` itself. For editors that carry real cost at
       * rest (a per-instance query, a heavy pool) in cells rendered once per
       * visible row. Trade-off: closing unmounts immediately, so the
       * overlay's exit animation is skipped.
       */
      defer?: boolean;
    }>
  | Omittable<{
      kind: "content";
      render: (api: EditableValueCellContentApi) => React.ReactNode;
    }>;

export type EditableValueCellProps = Omittable<{
  /** Opaque to the cell — only snapshotted for `reset`. */
  value: unknown;
  /** Rest display node (also the read-only body). */
  display: React.ReactNode;
  /** Drives the touch reveal gate: empty values skip straight to editing. */
  hasValue: boolean;
  editor?: EditableValueCellEditor;
  /** Accessible label for the rest trigger ("Edit Phone"). */
  triggerLabel?: string;
  /**
   * Cell height in px; with `grow` it is a minimum instead. Omit to fill the
   * host's cross axis: a table cell floats the ring with its own 2px inset,
   * so the cell is the row minus that inset at every density without the
   * host restating the row height. Property rows, which size themselves by
   * this cell, pass their rung.
   */
  height?: number;
  /** Content-sized cell (wrapping grids): `height` becomes `minHeight`. */
  grow?: boolean;
  /** Cell corners. Omit for the compact row treatment. */
  shape?: "square" | "pill";
  /**
   * Rest-state hover treatment. `outline` (default) rings the full cell —
   * the table grammar, where the ring is the edit promise. `fill` paints the
   * ghost-button fill across the row's width, value and actions together —
   * the property-row grammar, where the value sits beside ghost selector
   * triggers and must hover like them. `none` leaves the hover to the host
   * (a token that washes edge to edge with the cell inside it): no ring, no
   * capsule, a full-width trigger, actions pinned at the cell's end edge.
   * `token` is `none` for a host that HUGS its content (a board card's
   * token, sized to its value so the rest of the row stays the card's
   * press): the actions ride IN FLOW after the value and collapse at rest,
   * so the value keeps its full width until the pointer arrives and a
   * revealed action never lands on the value's tail — the token grows by
   * the actions while hovered.
   * Editing is unchanged: the themed-ring input still claims the row.
   */
  restHover?: "outline" | "fill" | "none" | "token";
  /**
   * The editing ring on the active inline editor. Off when the host paints
   * the editing state on its own chrome (a bordered token that turns its
   * edge brand while the cell is active, destructive while it is invalid —
   * read them from `isActive` and `onValidationErrorChange`). The cell then
   * reserves no border, so the editor sits exactly where the rest display
   * did. Inline editors only; overlay editors ring nothing.
   */
  outline?: boolean;
  /**
   * The inline inset the value sits at, in BOTH states. The rest trigger and
   * the editing cell wear the same padding, so entering edit mode never
   * shifts the text; editors rendered inside must carry no inset of their
   * own (a bare input, an input with its padding zeroed). Omit when the
   * display and the editor bring their own matching insets.
   */
  inset?: BoxProps["paddingX"];
  /**
   * Extend the cell 0.5rem past its container's end edge, into the host
   * row's own padding — the inline-ROW look, where the hover outline reads
   * wider than the value column. Pass false inside a hard-clipped container
   * (a table cell), where the overhang would clip the outline's end corners
   * square.
   */
  bleedEnd?: boolean;
  /** Display + actions only — no trigger, no editor, no activation. */
  readOnly?: boolean;
  actions?: readonly EditableValueCellAction[];
  /** Force-hide the value actions (e.g. while the host's row menu is up). */
  suppressActions?: boolean;
  /** Returns an error message to reject the commit, null to accept. */
  validate?: (value: unknown) => string | null;
  onCommit: (value: unknown) => void;
  /** Fires on every rejected commit attempt (host raises its feedback). */
  onInvalidCommit?: (message: string) => void;
  /** Invalid-state edges, for hosts that gate switching between cells. */
  onInvalidChange?: (invalid: boolean) => void;
  /** Validation message entering/leaving — feeds the host's label. */
  onValidationErrorChange?: (message: string | null) => void;
  /** Host-owned edit mode (inline editors only). */
  isActive?: boolean;
  onActivate?: () => void;
  onDeactivate?: () => void;
  /** Host-tracked highlight: while false, overlays and touch reveal dismiss. */
  highlighted?: boolean;
  /** Any overlay (picker, popover, touch reveal) opened or closed. */
  onOverlayChange?: (open: boolean) => void;
  /** Imperative hook for "Reset value"-style host chrome. */
  apiRef?: React.RefObject<EditableValueCellApi | null>;
}>;

export function EditableValueCell({
  value,
  display,
  hasValue,
  editor,
  triggerLabel = "Edit value",
  height,
  grow = false,
  shape = "square",
  restHover = "outline",
  outline = true,
  inset,
  bleedEnd = true,
  readOnly = false,
  actions,
  suppressActions = false,
  validate,
  onCommit,
  onInvalidCommit,
  onInvalidChange,
  onValidationErrorChange,
  isActive = false,
  onActivate,
  onDeactivate: onDeactivateRaw,
  highlighted = false,
  onOverlayChange,
  apiRef,
}: EditableValueCellProps): React.ReactElement {
  const [overlayOpen, setOverlayOpen] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );
  // An in-place refusal the inline editor reported. Kept apart from
  // `validationError`: it carries no `invalidRef`, so it never vetoes
  // deactivation.
  const [draftError, setDraftError] = React.useState<string | null>(null);
  // Synchronous gate for an editor's same-tick `commit` + `deactivate` pair.
  // Set in `commit` so the deactivation wrapper can veto before the
  // corresponding state update has flushed.
  const invalidRef = React.useRef(false);
  const cellRef = React.useRef<HTMLDivElement | null>(null);

  // Snapshot of the value at the moment edit mode was entered, so host
  // chrome can offer "reset" back to the last committed state.
  const baselineRef = React.useRef<unknown>(value);
  const wasActiveRef = React.useRef(isActive);
  React.useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      baselineRef.current = value;
    }
    wasActiveRef.current = isActive;
    // value intentionally omitted — the snapshot is taken at the entry edge,
    // not on every value change while active.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value is snapshotted at the entry edge only
  }, [isActive]);

  const deactivate = React.useCallback(() => {
    if (invalidRef.current) return;
    onDeactivateRaw?.();
  }, [onDeactivateRaw]);

  const commit = React.useCallback(
    (newValue: unknown) => {
      if (newValue === undefined) {
        invalidRef.current = false;
        onInvalidChange?.(false);
        setValidationError(null);
        onCommit(undefined);
        return;
      }
      const message = validate?.(newValue) ?? null;
      if (message !== null) {
        invalidRef.current = true;
        onInvalidChange?.(true);
        setValidationError(message);
        onInvalidCommit?.(message);
        return;
      }
      invalidRef.current = false;
      onInvalidChange?.(false);
      setValidationError(null);
      onCommit(newValue);
    },
    [onCommit, onInvalidChange, onInvalidCommit, validate],
  );

  // The host renders the label, so the error surfaces there — report edges.
  // A rejected commit outranks a draft refusal: it is the one that blocks.
  const surfacedError = validationError ?? draftError;
  const onValidationErrorChangeRef = React.useRef(onValidationErrorChange);
  onValidationErrorChangeRef.current = onValidationErrorChange;
  React.useEffect(() => {
    onValidationErrorChangeRef.current?.(surfacedError);
  }, [surfacedError]);

  // Tap-to-reveal only when the primary input has no hover (true
  // tablets/phones). Touchscreen laptops report a coarse pointer but still
  // hover, so the gate excludes them. An empty value has no value-actions,
  // so its first tap activates outright.
  const {
    isTouch,
    revealed,
    setRevealed,
    absorb,
    dismiss: dismissReveal,
  } = useRevealOnTouchGate({ enabled: hasValue, onRevealChange: onOverlayChange });

  const setOverlay = React.useCallback(
    (next: boolean) => {
      setOverlayOpen(next);
      onOverlayChange?.(next);
    },
    [onOverlayChange],
  );

  // Host moved the highlight elsewhere — dismiss without reporting (the
  // host already knows; it dropped the highlight).
  React.useEffect(() => {
    if (highlighted) return;
    setOverlayOpen(false);
    setRevealed(false);
  }, [highlighted, setRevealed]);

  // Clear stale validation state when the cell exits edit mode or the value
  // changes externally — otherwise the editor reverts on blur but the error
  // label sticks, and a stale invalidRef would veto the NEXT edit session's
  // first deactivation. The host is notified so its own invalid gate
  // releases even when deactivation happened outside the cell's control.
  const onInvalidChangeRef = React.useRef(onInvalidChange);
  onInvalidChangeRef.current = onInvalidChange;
  React.useEffect(() => {
    if (!isActive) {
      if (invalidRef.current) {
        invalidRef.current = false;
        onInvalidChangeRef.current?.(false);
      }
      setValidationError(null);
      setDraftError(null);
    }
  }, [isActive, value]);

  // Outside-click dismissal — touch reveal and inline-active editing.
  // Must also clear the host-tracked highlight, otherwise the cell stays
  // visually selected until another one is tapped.
  const handleCellOutside = React.useCallback(() => {
    if (isActive) {
      // An invalid draft never committed (`commit` withheld the write), so
      // clicking away is the give-up gesture: discard the draft and exit.
      // Without this branch the invalid veto in `deactivate` would lock the
      // cell in edit mode with no way out on hosts that render no reset
      // affordance.
      if (invalidRef.current) {
        invalidRef.current = false;
        onInvalidChange?.(false);
        setValidationError(null);
        onDeactivateRaw?.();
      } else {
        deactivate();
      }
    }
    if (isTouch && revealed) dismissReveal();
  }, [
    deactivate,
    dismissReveal,
    isActive,
    isTouch,
    onDeactivateRaw,
    onInvalidChange,
    revealed,
  ]);
  useOutsideClick({
    ref: cellRef,
    active: isActive || (isTouch && revealed),
    onOutside: handleCellOutside,
  });

  const overwrite = React.useCallback(
    (newValue: unknown) => {
      // Blur FIRST: a still-focused editor flushes its blur-commit now, so
      // the write below lands last instead of being clobbered by the draft.
      blurActiveElement();
      invalidRef.current = false;
      onInvalidChange?.(false);
      setValidationError(null);
      setOverlay(false);
      onCommit(newValue);
      onDeactivateRaw?.();
    },
    [onCommit, onDeactivateRaw, onInvalidChange, setOverlay],
  );

  const reset = React.useCallback(() => {
    overwrite(baselineRef.current);
  }, [overwrite]);

  const cellApi = React.useMemo<EditableValueCellApi>(
    () => ({ commit, deactivate, overwrite, reset }),
    [commit, deactivate, overwrite, reset],
  );

  React.useEffect(() => {
    if (!apiRef) return;
    apiRef.current = cellApi;
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, cellApi]);

  const actionsVisibility: InlineRowActionsVisibility = (() => {
    if (
      !actions ||
      actions.length === 0 ||
      isActive ||
      overlayOpen ||
      suppressActions
    )
      return "hidden";
    if (revealed) return "always";
    return "valueHoverFocus";
  })();

  // Outline grammar: the actions float over the value's tail, so they fuse
  // into ONE chip landing on the text. Fill grammar: the capsule already
  // groups value and actions on one filled pill, so each action stays a
  // free-standing chip in flow.
  const fillActions = restHover === "fill" && actions !== undefined && actions.length > 0;
  // Token grammar: the actions ride in flow like the fill grammar's and
  // COLLAPSE at rest — the value keeps every pixel until the pointer
  // arrives, then the token grows by the actions (or a row-filling value
  // truncates by them) while it is hovered. A slot held at rest truncated
  // the value for buttons nobody was looking at (Spencer, 2026-09-28).
  // Free-standing ghosts, no chip: the host's own wash is the capsule they
  // reveal on; the trailing 4px seats the last action off the token's edge
  // and, riding on the row, appears only with it.
  const flowActions = restHover === "token" && actions !== undefined && actions.length > 0;

  const actionsRow =
    actions && actions.length > 0 ? (
      <InlineRowActions
        chrome={fillActions || flowActions ? undefined : "chip"}
        collapse={fillActions || flowActions}
        shrink={false}
        visibility={actionsVisibility}
        paddingRight={flowActions ? "2xs" : undefined}
      >
        {actions.map((action) => (
          <InlineValueAction
            key={action.key}
            icon={action.icon}
            label={action.label}
            tooltip={action.tooltip}
            onSelect={() => action.onSelect(cellApi)}
          />
        ))}
      </InlineRowActions>
    ) : null;

  // Fill grammar: the actions ride IN the fill capsule with the value, so they reveal on the filled pill rather than floating at the
  // cell's far edge (where a long value leaves them straddling the pill's
  // end). Outline grammar keeps them pinned inside the ringed cell's edge —
  // the ring spans the full cell, so the edge IS the hover area.
  const actionsNode =
    actionsRow !== null && !fillActions && !flowActions ? (
      <Box position="absolute" right={4} top={0} bottom={0} justify="center">
        {actionsRow}
      </Box>
    ) : null;

  const sizing =
    height === undefined
      ? undefined
      : grow
        ? { minHeight: height }
        : { height };

  // An open overlay pins the rest cell's hover ring: the pointer and focus
  // have left for the popup, but the cell is still the field being edited.
  // `paintedTrigger` is false for bodies with no edit promise (read-only, an
  // in-place content editor): the capsule still holds the actions beside the
  // value, but paints no fill.
  const restCell = (body: React.ReactNode, paintedTrigger = true) => (
    <InlineValueCell
      {...sizing}
      bleedEnd={bleedEnd}
      shape={shape}
      hover={restHover === "token" ? "none" : restHover}
      active={overlayOpen}
    >
      {restHover === "fill" ? (
        <InlineValueFillGroup paint={paintedTrigger}>
          {body}
          {actionsRow}
        </InlineValueFillGroup>
      ) : flowActions ? (
        <>
          {body}
          {actionsRow}
        </>
      ) : (
        <>
          {body}
          {actionsNode}
        </>
      )}
    </InlineValueCell>
  );


  // A body with no trigger (read-only, in-place content) takes the inset on
  // a wrapper; a trigger carries it itself, so the whole cell stays pressable.
  const insetBody = (body: React.ReactNode) =>
    inset === undefined ? (
      body
    ) : (
      <Box fill="flex" minWidth={0} direction="row" align="center" paddingX={inset}>
        {body}
      </Box>
    );

  const renderBody = (): React.ReactNode => {
    // Read-only short-circuit: plain display, no trigger, no editor, no
    // activation — but still inside `InlineValueCell` so hover-revealed
    // actions (a copy button) keep working.
    if (readOnly || !editor) return restCell(insetBody(display), false);

    if (editor.kind === "content") {
      return restCell(insetBody(editor.render({ commit })), false);
    }

    if (editor.kind === "inline") {
      if (isActive) {
        const editingCell = (
          <InlineEditingCell
            {...sizing}
            bleedEnd={bleedEnd}
            shape={shape}
            paddingX={inset}
            invalid={surfacedError !== null}
            outline={outline}
            // Escape leaves the editor and goes no further: a dialog hosting
            // the cell listens for the same key on the document, and without
            // the stop a first Escape while typing closes the whole dialog.
            // The editor's own handler (a draft to discard) runs first, in
            // the bubble; the deactivate here is the fallback for editors
            // that have none.
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key !== "Escape") return;
              event.stopPropagation();
              deactivate();
            }}
          />
        );
        // A rejected COMMIT is explained on the cell itself, the way an
        // editor that refuses a keystroke explains its own draft error — so
        // every field answers a bad value in the same place. Controlled
        // open: the bubble shows while the error stands, not only on hover.
        // A draft error is the editor's to explain (it already does), so it
        // never opens a second bubble here.
        return (
          <Tooltip open={validationError !== null}>
            <TooltipTrigger render={editingCell}>
              {editor.render({
                commit,
                deactivate,
                invalid: surfacedError !== null,
                reportDraftError: setDraftError,
              })}
            </TooltipTrigger>
            {validationError !== null ? (
              <TooltipPopup side="top" align="start">
                {validationError}
              </TooltipPopup>
            ) : null}
          </Tooltip>
        );
      }
      return restCell(
        <InlineValueTrigger
          aria-label={triggerLabel}
          paddingX={inset}
          onClick={() => {
            if (absorb(true)) return;
            onActivate?.();
          }}
        >
          {display}
        </InlineValueTrigger>,
      );
    }

    const requestOpenChange = (next: boolean): boolean => {
      if (absorb(next)) return false;
      setOverlay(next);
      return true;
    };
    const cellWithTrigger = (onClick?: () => void) =>
      restCell(
        <InlineValueTrigger
          aria-label={triggerLabel}
          paddingX={inset}
          onClick={onClick ?? (() => requestOpenChange(true))}
        >
          {display}
        </InlineValueTrigger>,
      );
    if (editor.defer && !overlayOpen) return cellWithTrigger();
    return editor.render({
      open: overlayOpen,
      onOpenChange: requestOpenChange,
      close: () => setOverlay(false),
      commit,
      deactivate,
      cellWithTriggerPart: (part) =>
        restCell(
          React.cloneElement(part, {
            render: (
              <InlineValueTrigger
                aria-label={triggerLabel}
                      paddingX={inset}
              >
                {display}
              </InlineValueTrigger>
            ),
          }),
        ),
      cellWithTrigger,
    });
  };

  // The outside-click boundary for deactivation/dismissal. `fill="flex"` +
  // stretch so the InlineValueCell inside sizes exactly as it does against a
  // bare flex host.
  return (
    <Box
      ref={cellRef}
      fill="flex"
      minWidth={0}
      direction="row"
      align="stretch"
    >
      {renderBody()}
    </Box>
  );
}
